import { Add, Edit } from '@mui/icons-material'
import { Button, Checkbox, TableCell, Typography } from '@mui/material'
import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { BaseUser } from '../../../../../api/DTO'
import { GroupUserSimpleAssociation } from '../../../../../api/DTOtemp'
import { GroupUserAssociationsClient } from '../../../../../api/clients/identity'
import { CurrentOrganizationUsersClient } from '../../../../../api/clients/identity/CurrentOrganizationUsersClient'
import IconButtonLink from '../../../../../components/links/IconButtonLink'
import { TableColumn } from '../../../../../components/tables/DTO'
import { FilteredTable } from '../../../../../components/tables/FilteredTable'
import { FilterControl } from '../../../../../components/tables/filters/DTO'
import { toastService } from '../../../../../services/notifications/ToastService'
import { makeChildRoute } from '../../../../../utils/CreateChildRoute'
import pluralizeString from '../../../../../utils/PluralizeString'
import AdministrationPageContainer from '../../AdministrationPageContainer'
import { AddValuesDialog } from '../AddValuesDialog'
import { GroupAdministrationPageProps } from '../GroupPage'

export const GroupUsersAdministrationPage = observer(
	({ group, tabLabels, onTabChanged }: GroupAdministrationPageProps) => {
		const groupUserClient = new GroupUserAssociationsClient()

		const localStore = useLocalObservable(() => ({
			groupUsers: [] as BaseUser[],
			organizationUsers: [] as BaseUser[],

			// get the users in the organization that don't belong in the group to display when adding new users
			get filteredOrganizationUsers() {
				return this.organizationUsers.filter(
					(orgUser) =>
						!this.groupUsers
							.map((groupUser) => groupUser.id)
							.includes(orgUser.id),
				)
			},
			isRequesting: false,
			selectedUsers: [] as number[],
		}))

		const getGroupUsers = useCallback(() => {
			return groupUserClient
				.GetGroupUserAssociations(undefined, group.id)
				.then((response) => response.data.map((v) => v.user))
		}, [])

		const getOrganizationUsers = useCallback(() => {
			const organizationUsersClient = new CurrentOrganizationUsersClient()

			return organizationUsersClient
				.getUsersForCurrentOrganization(undefined, {
					'cache-control': 'reload',
				})
				.then((response) => response.data.map((v) => v))
		}, [])

		useEffect(() => {
			; (async () => {
				const groupUsersPromise = getGroupUsers()
				const organizationUsersPromise = getOrganizationUsers()

				runInAction(() => {
					localStore.isRequesting = true
				})

				const groupUsers = await groupUsersPromise
				const organizationUsers = await organizationUsersPromise

				runInAction(() => {
					localStore.groupUsers = groupUsers
					localStore.organizationUsers = organizationUsers
					localStore.isRequesting = false
				})
			})()

			getGroupUsers()
		}, [])

		const handleAddUsers = async (users: BaseUser[]) => {
			const associations: GroupUserSimpleAssociation[] = []
			for (const user of users)
				associations.push({ userId: user.id, groupId: group.id })

			try {
				runInAction(() => {
					localStore.isRequesting = true
				})
				const addedCount = (
					await groupUserClient.BulkAddGroupUserAssociation(associations)
				).data
				const groupUsers = await getGroupUsers()

				runInAction(() => {
					localStore.groupUsers = groupUsers
				})

				toastService.displayToast({
					message: `${pluralizeString(addedCount, 'user')} added`,
					area: 'global',
					severity: 'success',
				})
			} catch {
				toastService.displayToast({
					message: 'Error adding users',
					area: 'global',
					severity: 'error',
				})
			} finally {
				runInAction(() => {
					localStore.isRequesting = false
				})
			}
		}

		const handleRemoveUsers = async () => {
			try {
				runInAction(() => {
					localStore.isRequesting = true
				})

				// get the ids of the associations to remove
				const { data } = await groupUserClient.GetGroupUserAssociations(
					undefined,
					group.id,
				)

				const associationsToRemove = data
					.filter((v) => localStore.selectedUsers.includes(v.userId))
					.map((v) => v.id)

				const removedCount = (
					await groupUserClient.BulkDeleteGroupUserAssociations(
						associationsToRemove,
					)
				).data

				const groupUsers = await getGroupUsers()

				runInAction(() => {
					localStore.groupUsers = groupUsers
				})

				toastService.displayToast({
					message: `${pluralizeString(removedCount, 'user')} removed`,
					area: 'global',
					severity: 'success',
				})

				localStore.selectedUsers = []
			} catch {
				toastService.displayToast({
					message: 'Error removing users',
					area: 'global',
					severity: 'error',
				})
			} finally {
				runInAction(() => {
					localStore.isRequesting = false
				})
			}
		}

		// select if unselected, unselect if selected
		const handleSelectOneUser = action((userId: number) => {
			if (!localStore.selectedUsers.includes(userId)) {
				localStore.selectedUsers.push(userId)
				return
			}
			const index = localStore.selectedUsers.indexOf(userId)
			if (index > -1) localStore.selectedUsers.splice(index, 1)
		})

		// select all if some are unselected, unselect all if all are already selected
		const handleSelectAllUsers = action(
			(event: React.SyntheticEvent<HTMLInputElement>) => {
				// console.log(localStore.selectedUsers)
				// console.log(localStore.groupUsers)
				// console.log(event.currentTarget.checked)
				// console.log(localStore.groupUsers.map((user) => user.id))
				if (event.currentTarget.checked)
					localStore.selectedUsers = localStore.groupUsers.map((user) => user.id)
				else localStore.selectedUsers = []
			},
		)

		const userIsChecked = useCallback(
			(userId: number) => {
				return localStore.selectedUsers.includes(userId)
			},
			[localStore.selectedUsers],
		)

		// header checkbox is checked if all users are selected
		const headerCheckboxChecked = useCallback(() => {
			// console.log(localStore.groupUsers.length)
			// console.log(localStore.selectedUsers.length)
			return (
				localStore.groupUsers.length === localStore.selectedUsers.length && localStore.groupUsers.length !== 0
			)
		}, [localStore.groupUsers, localStore.selectedUsers])

		// header checkbox is indeterminate if some users are selected
		const headerCheckboxIndeterminate = useCallback(() => {
			return (
				localStore.selectedUsers.length !== 0 &&
				localStore.groupUsers.length !== localStore.selectedUsers.length
			)
		}, [localStore.groupUsers, localStore.selectedUsers])

		const filterControls: FilterControl[] = useMemo(() => [
			{
				type: 'search',
				name: 'search',
				column: ["firstName", "lastName", "emailAddress"]
			}
		], [localStore.groupUsers])

		const columns: TableColumn<BaseUser>[] = [
			{
				renderHeadingDynamic: () =>
					<TableCell key={0} padding="checkbox">
						<Checkbox
							color="primary"
							checked={headerCheckboxChecked()}
							indeterminate={headerCheckboxIndeterminate()}
							onChange={handleSelectAllUsers}
						/>
					</TableCell>,
				renderCell: (user: BaseUser) =>
					<TableCell key={0} padding="checkbox">
						<Checkbox
							color="primary"
							checked={userIsChecked(user.id)}
							value={userIsChecked(user.id)}
							onChange={() => handleSelectOneUser(user.id)}
						/>
					</TableCell>
			},
			{
				name: "firstName",
				renderHeadingDynamic: () => <TableCell key={1}>
					{localStore.selectedUsers.length === 0 ? "First Name" :
						<Button
							color="primary"
							variant="outlined"
							disabled={localStore.isRequesting}
							onClick={handleRemoveUsers}
							size="small"
						>
							Remove
						</Button>
					}
				</TableCell>
			},
			{
				name: "lastName",
				renderHeadingDynamic: () => <TableCell key={2}>
					{localStore.selectedUsers.length === 0 ? "Last Name" : ""}
				</TableCell>
			},
			{
				name: "emailAddress",
				renderHeadingDynamic: () => <TableCell key={3}>
					{localStore.selectedUsers.length === 0 ? "Email" : ""}
				</TableCell>
			},
			{
				renderHeadingDynamic: () => <TableCell key={4} align="right">
					{localStore.selectedUsers.length === 0 ? "Actions" : ""}
				</TableCell>,
				renderCell: (user: BaseUser) =>
					<TableCell key={4} align="right">
						<IconButtonLink
							to={makeChildRoute(
								`${user.id}`,
								`/_administration/_users`,
							)}
						>
							<Edit />
						</IconButtonLink>
					</TableCell>
			}
		]

		return (
			<AdministrationPageContainer
				title={group.name}
				tabs={tabLabels}
				currentTab={1}
				onTabChanged={onTabChanged}
				actions={
					<GroupUsersSectionButtons
						groupName={group.name}
						onAddUsers={handleAddUsers}
						filteredOrganizationUsers={localStore.filteredOrganizationUsers}
						isRequesting={localStore.isRequesting}
					/>
				}
			>
				<FilteredTable<BaseUser>
					columns={columns}
					allEntries={localStore.groupUsers}
					filterControls={filterControls}
				/>
			</AdministrationPageContainer>
		)
	},
)

type GroupUsersSectionButtonsProps = {
	groupName: string
	filteredOrganizationUsers: BaseUser[]
	onAddUsers: (users: BaseUser[]) => void
	isRequesting: boolean
}

const GroupUsersSectionButtons = observer(
	(props: GroupUsersSectionButtonsProps) => {
		const [addUserOpen, setAddUserOpen] = useState(false)

		const handleOpenAddUser = useCallback(() => {
			setAddUserOpen(true)
		}, [])

		const handleCloseAddUser = useCallback(() => {
			setAddUserOpen(false)
		}, [])

		const handleAddUsers = useCallback((users: BaseUser[]) => {
			props.onAddUsers(users)
			setAddUserOpen(false)
		}, [])

		return (
			<div>
				<Button
					color="primary"
					variant="contained"
					startIcon={<Add />}
					onClick={handleOpenAddUser}
					disabled={props.isRequesting}
				>
					Add Users
				</Button>
				<AddValuesDialog<BaseUser>
					open={addUserOpen}
					onClose={handleCloseAddUser}
					getLabel={(v: BaseUser) => `${v.firstName} ${v.lastName}`}
					header={`Add Users to ${props.groupName}`}
					renderOption={(user) => {
						return (
							<>
								<Typography color="textPrimary" variant="subtitle2">
									{user.firstName} {user.lastName}
								</Typography>
								<Typography color="textSecondary" variant="body2">
									{user.emailAddress}
								</Typography>
							</>
						)
					}}
					options={props.filteredOrganizationUsers}
					onCommit={handleAddUsers}
					placeholder="Search users"
					addText="Add Users"
				/>
			</div>
		)
	},
)
